<template>
  <v-container fluid class="pa-5">
    <v-row align="center" justify="center">
      <v-col cols="6">
        <v-text-field
          v-model="search"
          outlined
          solo
          class="rounded-lg"
          placeholder="search reports..."
          append-icon="search"
        />
      </v-col>
      <v-col cols="10" v-if="!commentsToDisplay.length">
        <h4 class="text-center font-weight-bold primary--text">No reported comments...</h4>
      </v-col>
      <v-col cols="10"
        v-for="comment in commentsToDisplay"
        :key="comment.id"
      >
        <v-card>
          <v-container fluid>
            <v-row align="center" justify="start" no-gutters class="pa-2">
              <v-col cols="12" class="mb-2">
                <span>Forum Topic: </span>
                <span class="font-weight-medium primary--text">{{ comment.topic.title }}</span>
              </v-col>
              <v-col cols="12" class="mb-2">
                <span>Reported By: </span>
                <span class="font-weight-bold primary--text">{{ comment.reporter.name }}</span>
              </v-col>
              <v-col cols="12" class="mb-2">
                <span>Reported At: </span>
                <span class="font-weight-bold primary--text">{{ comment.reportedAt }}</span>
              </v-col>
              <v-col cols="12">
                <span>Reason: </span>
                <span class="font-weight-medium font-italic primary--text">{{ comment.reportMessage }}</span>
              </v-col>
            </v-row>
          </v-container>

          <v-divider class="my-2" />

          <v-container fluid>
            <div class="justify-start d-flex d-flex-row align-start">
              <v-avatar
                rounded="full"
                :size="$vuetify.breakpoint.smAndDown ? 50 : 80"
                left
                class="mr-4"
                color="primary"
              >
                <v-img
                  v-if="comment.user.image"
                  :src="comment.user.image"
                  :alt="comment.user.name"
                  :lazy-src="require('@/assets/img/logo.png')"
                  eager
                />
                <p v-else class="white--text text-h3 font-weight-semibold ma-auto">
                  {{ userInitial }}
                </p>
              </v-avatar>
              <v-card outlined width="100%" class="pa-2" rounded="lg">
                <v-card-text class="flex-row text-subtitle-2 d-flex">
                  {{ comment.user.name }} ● {{ comment.date }}
                </v-card-text>
                <v-card-subtitle
                  class="flex-row justify-start black--text text-subtitle-1 mt-n4"
                >
                  <span v-html="comment.body"></span>
                </v-card-subtitle>
              </v-card>
            </div>
          </v-container>

          <v-card-actions>
            <v-row align="center" justify="end">
              <v-spacer />
              <v-col cols="auto">
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      color="success"
                      @click="reapproveComment(comment.id)"
                      v-on="on"
                      v-bind="attrs"
                      large
                      :loading="isLoading"
                    >
                      <v-icon>visibility</v-icon>
                    </v-btn>
                  </template>
                  <span>Re-Approve Comment</span>
                </v-tooltip>
              </v-col>
              <v-col cols="auto">
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      color="error"
                      @click="deleteComment(comment.id)"
                      v-on="on"
                      v-bind="attrs"
                      large
                      :loading="isLoading"
                    >
                      <v-icon>delete</v-icon>
                    </v-btn>
                  </template>
                  <span>Delete Comment</span>
                </v-tooltip>
              </v-col>
            </v-row>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>

  </v-container>
</template>

<script>
import { formatDate, formatToElapsedTime } from '@/utils/date'
export default {
  name: 'ReportComments',
  components: {
  },
  async created() {
    await this.$store.dispatch('comments/SUBSCRIBE_TO_REPORTED');
  },
  async beforeDestroy() {
    await this.$store.dispatch('comments/UNSUBSCRIBE_TO_REPORTED');
  },
  data: () => ({
    search: '',
    isLoading: false
  }),
  computed: {
    reportedComments() {
      const comments = this.$store.getters['comments/REPORTED_COMMENTS'];
      return comments.map((comment) => ({
        ...comment,
        date: formatToElapsedTime(comment.date),
        searchableDate: formatDate(comment.date, 'MMM DD, YYYY @ hh:mmA'),
        reportedAt: formatDate(comment.reportedAt, 'MMM DD, YYYY @ hh:mmA')
      }))
    },
    commentsToDisplay() {
      const comments = [...this.reportedComments];
      if (this.search) {
        const search = this.search.toLowerCase();
        return comments.filter(
          (comment) =>
            comment.body.toLowerCase().includes(search) ||
            comment.user.name.toLowerCase().includes(search) ||
            comment.searchableDate.toLowerCase().includes(search) ||
            comment.reporter.name.toLowerCase().includes(search) ||
            comment.reportedAt.toLowerCase().includes(search) ||
            comment.forum.title.toLowerCase().includes(search)
        )
      }
      return comments;
    }
  },
  methods: {
    formatDate,
    formatToElapsedTime,
    async reapproveComment(id) {
      this.isLoading = true;
      const response = await this.$swal.fire({
        icon: 'warning',
        html: `
          <p>Are you sure you want to re-approve this comment?</p>
          <br />
          <p>Once re-approved, this comment will re-appear in the comments section of its designated forum topic.</p>
        `,
        showCancelButton: true,
        confirmButtonText: 'Re-approve this comment!'
      })

      if (response.value) {
        await this.confirmApproval(id)
      }
      this.isLoading = false;
    },
    async confirmApproval(id) {
      this.isLoading = true;
      try {
        await this.$store.dispatch('comments/REAPPROVE_COMMENT', { id });
        this.$swal.fire({
          icon: 'success',
          text: 'The comment has been re-approved!'
        });
      } catch (err) {
        console.log('ERR IN RE-APPROVING COMMENT: ', err);
        this.$swal.fire({
          icon: 'success',
          text: 'Something went wrong, please try again later.'
        });
      }
      this.isLoading = false;
    },
    async deleteComment(id) {
      this.isLoading = true;
      const response = await this.$swal.fire({
        icon: 'warning',
        html: `
          <p>Are you sure you want to delete this comment?</p>
          <br />
          <p>Once deleted, this comment will be permanently removed from its designated forum topic.</p>
        `,
        showCancelButton: true,
        confirmButtonText: 'Delete this comment!'
      })

      if (response.value) {
        await this.confirmDeletion(id)
      }
      this.isLoading = false;
    },
    async confirmDeletion(id) {
      this.isLoading = true;
      try {
        await this.$store.dispatch('comments/DELETE_COMMENT', { id });
        this.$swal.fire({
          icon: 'success',
          text: 'The comment has been deleted!'
        });
      } catch (err) {
        console.log('ERR IN DELETING COMMENT: ', err);
        this.$swal.fire({
          icon: 'success',
          text: 'Something went wrong, please try again later.'
        });
      }
      this.isLoading = false;
    }
  }
}
</script>

<style>

</style>